import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { getDashboardByRole } from "@/utils/routeUtils";

interface UseAuthProtectionProps {
  requiredRole?: "admin" | "host" | "guest";
  isPublic?: boolean;
}

export const useAuthProtection = ({ requiredRole, isPublic = false }: UseAuthProtectionProps) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    const checkAuth = async () => {
      try {
        console.log("Checking auth...");
        
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error("Session error:", sessionError);
          throw new Error("Authentication failed");
        }

        if (!session) {
          if (!isPublic) {
            await supabase.auth.signOut();
            
            toast({
              title: "Session expired",
              description: "Please sign in again",
              variant: "destructive",
            });
            
            localStorage.setItem('returnTo', window.location.pathname);
            navigate("/login");
            return;
          }
        }

        if (isPublic) {
          console.log("Public route, allowing access");
          if (mounted) setIsLoading(false);
          return;
        }

        if (!session) {
          console.log("No session found, redirecting to login");
          toast({
            title: "Authentication required",
            description: "Please log in to continue",
            variant: "destructive",
          });
          localStorage.setItem('returnTo', window.location.pathname);
          navigate("/login");
          return;
        }

        if (requiredRole) {
          console.log("Checking role:", requiredRole);
          const { data: profile, error: profileError } = await supabase
            .from("profiles")
            .select("role")
            .eq("id", session.user.id)
            .single();

          if (profileError) {
            console.error("Profile error:", profileError);
            throw new Error("Failed to verify user role");
          }

          console.log("User role:", profile.role);
          
          // Special case: Allow guests to access host onboarding
          const isHostOnboarding = window.location.pathname === "/host/onboarding";
          if (isHostOnboarding && profile.role === "guest") {
            console.log("Allowing guest access to host onboarding");
            if (mounted) setIsLoading(false);
            return;
          }

          if (profile.role !== requiredRole) {
            console.log("Invalid role, redirecting");
            toast({
              title: "Access denied",
              description: "You don't have permission to access this page",
              variant: "destructive",
            });
            navigate(getDashboardByRole(profile.role));
            return;
          }
        }

        if (mounted) setIsLoading(false);
      } catch (error) {
        console.error("Auth error:", error);
        if (mounted) {
          setError("Authentication error");
          if (!isPublic) {
            await supabase.auth.signOut();
            
            toast({
              title: "Authentication error",
              description: "Please try logging in again",
              variant: "destructive",
            });
            localStorage.setItem('returnTo', window.location.pathname);
            navigate("/login");
          }
        }
      }
    };

    checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log("Auth state changed:", event);
      if (event === 'SIGNED_OUT' && !isPublic) {
        navigate('/login');
      } else if (event === 'TOKEN_REFRESHED' || event === 'SIGNED_IN') {
        checkAuth();
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [navigate, requiredRole, isPublic, toast]);

  return { isLoading, error };
};